import React, { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Input } from './input';
import { Button } from './button';
import { Mic, MicOff, X, MapPin, Clock, Star } from 'lucide-react';

interface SmartSuggestion {
  id: string;
  text: string;
  type: 'location' | 'recent' | 'frequent' | 'contact' | 'prediction';
  icon?: React.ReactNode;
  metadata?: { description?: string };
}

interface SmartInputProps extends Omit<React.ComponentProps<"input">, 'onChange'> {
  suggestions?: SmartSuggestion[];
  onSuggestionSelect?: (suggestion: SmartSuggestion) => void;
  onChange?: (value: string) => void;
  onVoiceInput?: (transcript: string) => void;
  showVoiceButton?: boolean;
  showClearButton?: boolean;
  maxSuggestions?: number;
  enableVoiceToText?: boolean;
  contextualSuggestions?: boolean;
}

declare global {
  interface Window {
    SpeechRecognition: unknown;
    webkitSpeechRecognition: unknown;
  }
}

export const SmartInput: React.FC<SmartInputProps> = ({
  suggestions = [],
  onSuggestionSelect,
  onChange,
  onVoiceInput,
  showVoiceButton = true,
  showClearButton = true,
  maxSuggestions = 5,
  enableVoiceToText = true,
  contextualSuggestions = true,
  className,
  value,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  ...props
}) => {
  const [isListening, setIsListening] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<SmartSuggestion[]>([]);
  const [recognition, setRecognition] = useState<{ start: () => void; stop: () => void } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize speech recognition
  useEffect(() => {
    if (enableVoiceToText && typeof window !== 'undefined') {
      const SpeechRecognition = (window.SpeechRecognition || window.webkitSpeechRecognition) as
        | (new () => {
            continuous: boolean;
            interimResults: boolean;
            lang: string;
            onresult: (event: { results: { transcript: string }[][] }) => void;
            onerror: () => void;
            onend: () => void;
            start: () => void;
            stop: () => void;
          })
        | undefined;
      
      if (SpeechRecognition) {
        const recognitionInstance = new SpeechRecognition();
        recognitionInstance.continuous = false;
        recognitionInstance.interimResults = false;
        recognitionInstance.lang = 'en-US';

        recognitionInstance.onresult = (event) => {
          const transcript = event.results[0][0].transcript;
          onChange?.(transcript);
          onVoiceInput?.(transcript);
          setIsListening(false);
        };

        recognitionInstance.onerror = () => {
          setIsListening(false);
        };

        recognitionInstance.onend = () => {
          setIsListening(false);
        };

        setRecognition(recognitionInstance);
      }
    }
  }, [enableVoiceToText, onChange, onVoiceInput]);

  // Filter suggestions based on input value
  useEffect(() => {
    if (!value || !contextualSuggestions) {
      setFilteredSuggestions(suggestions.slice(0, maxSuggestions));
      return;
    }

    const filtered = suggestions
      .filter(suggestion =>
        suggestion.text.toLowerCase().includes(value.toString().toLowerCase())
      )
      .slice(0, maxSuggestions);

    setFilteredSuggestions(filtered);
  }, [value, suggestions, maxSuggestions, contextualSuggestions]);

  // Handle voice input
  const handleVoiceToggle = useCallback(() => {
    if (!recognition) return;

    if (isListening) {
      recognition.stop();
      setIsListening(false);
    } else {
      recognition.start();
      setIsListening(true);
    }
  }, [recognition, isListening]);

  // Handle input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange?.(newValue);
    setShowSuggestions(newValue.length > 0 && filteredSuggestions.length > 0);
  }, [onChange, filteredSuggestions.length]);

  // Handle suggestion selection
  const handleSuggestionClick = useCallback((suggestion: SmartSuggestion) => {
    onChange?.(suggestion.text);
    onSuggestionSelect?.(suggestion);
    setShowSuggestions(false);
    inputRef.current?.blur();
  }, [onChange, onSuggestionSelect]);

  // Handle clear input
  const handleClear = useCallback(() => {
    onChange?.('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  }, [onChange]);

  // Handle focus and blur
  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    if (filteredSuggestions.length > 0) {
      setShowSuggestions(true);
    }
    onFocusProp?.(e);
  }, [filteredSuggestions.length, onFocusProp]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    // Delay hiding suggestions to allow for suggestion clicks
    setTimeout(() => setShowSuggestions(false), 150);
    onBlurProp?.(e);
  }, [onBlurProp]);

  // Get suggestion icon
  const getSuggestionIcon = (suggestion: SmartSuggestion) => {
    if (suggestion.icon) return suggestion.icon;
    
    switch (suggestion.type) {
      case 'location':
        return <MapPin className="h-4 w-4 text-primary" />;
      case 'recent':
        return <Clock className="h-4 w-4 text-muted-foreground" />;
      case 'frequent':
        return <Star className="h-4 w-4 text-amber-500" />;
      default:
        return <MapPin className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={cn(
            "pr-20 touch-manipulation",
            isListening && "ring-2 ring-primary ring-opacity-50 border-primary",
            className
          )}
          {...props}
        />
        
        {/* Action buttons */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-1">
          {showClearButton && value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="h-8 w-8 p-0 hover:bg-muted rounded-full"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          
          {showVoiceButton && recognition && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleVoiceToggle}
              className={cn(
                "h-8 w-8 p-0 rounded-full transition-colors",
                isListening 
                  ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                  : "hover:bg-muted"
              )}
            >
              {isListening ? (
                <MicOff className="h-4 w-4 animate-pulse" />
              ) : (
                <Mic className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-background border rounded-lg shadow-lg overflow-hidden animate-slide-in-bottom">
          <div className="max-h-60 overflow-y-auto">
            {filteredSuggestions.map((suggestion, index) => (
              <button
                key={`${suggestion.id}-${index}`}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-muted transition-colors border-b last:border-b-0 touch-manipulation"
              >
                {getSuggestionIcon(suggestion)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{suggestion.text}</p>
                  {suggestion.metadata?.description && (
                    <p className="text-xs text-muted-foreground truncate">
                      {suggestion.metadata.description}
                    </p>
                  )}
                </div>
                <div className="text-xs text-muted-foreground capitalize">
                  {suggestion.type}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};